import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { optinColors, optinLabels } from "../externalPages/contacts/shared";
import { Tooltip } from "./Tooltip";
import { Badge } from "./tailwind/badge";
import { PencilIcon } from "@heroicons/react/24/solid";
import { Button } from "./tailwind/button";

export default function KeyValue({
  index,
  label,
  value,
  isArray,
  isRaw,
  onClickFunction,
}) {
  const labelRef = useRef(null);
  const valueRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const computeOverflow = useCallback(() => {
    const valueElement = valueRef.current;
    let hasOverflow = false;
    if (valueElement) {
      hasOverflow = valueElement.scrollWidth > valueElement.clientWidth;
    }
    if (!isRaw && !isArray && !hasOverflow) {
      const labelElement = labelRef.current;
      if (labelElement) {
        hasOverflow = labelElement.scrollWidth > labelElement.clientWidth;
      }
    }
    setIsOverflow(hasOverflow);
  }, [valueRef, labelRef, isArray, isRaw]);

  useEffect(() => {
    computeOverflow();
    window.addEventListener("resize", computeOverflow);
    return () => window.removeEventListener("resize", computeOverflow);
  }, [value]);

  return (
    <div data-testid={`key-value-${index}`} className="flex w-full">
      <Tooltip
        size="4"
        iconClassName={clsx("-ml-[20px] mt-[3px]", isRaw && "ml-0 mr-[10px]")}
        popoverClassName="w-max max-w-[500px]"
        content={
          isOverflow ? (
            <div className="break-all">
              {!isArray && !isRaw && (
                <div className="font-bold mr-2 flex-shrink-0">{label}</div>
              )}
              <span className="flex-1 min-w-0 text-green-600">{value}</span>
            </div>
          ) : undefined
        }
      />
      {!isArray && !isRaw && isOverflow && <>&nbsp;</>}

      {isArray ? (
        <Badge className="w-full">
          <span
            ref={valueRef}
            className="flex-1 min-w-0 text-green-600 overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {value}
          </span>
        </Badge>
      ) : isRaw ? (
        <span
          ref={valueRef}
          className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {value}
        </span>
      ) : (
        <>
          <span
            data-testid="key"
            ref={labelRef}
            className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-bold mr-2 flex-shrink-0"
          >
            {label}
          </span>
          {optinLabels[value] ? (
            <span
              data-testid="value"
              ref={valueRef}
              className="flex-1 min-w-0 text-green-600 overflow-hidden text-ellipsis whitespace-nowrap inline-flex align-center"
            >
              <Badge color={optinColors[value]} className="h-5">
                {optinLabels[value]}
              </Badge>
              {onClickFunction && (
                <Button outline onClick={onClickFunction} className="h-5">
                  <PencilIcon />
                </Button>
              )}
            </span>
          ) : (
            <span
              data-testid="value"
              ref={valueRef}
              className="flex-1 min-w-0 text-green-600 overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {value}
            </span>
          )}
        </>
      )}
    </div>
  );
}
